import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild
} from '@angular/core';
import {selectSelectedProject} from '../../../project/service/project.selectors';
import {selectPapersByProjectId} from '../../services/research-paper/research-paper.selectors';
import {Store} from '@ngrx/store';
import {of} from 'rxjs';
import {MetricsService} from '../../../../utils/services/metrics.service';
import {ResearchPaperDTO} from '../../dto/research-paper';
import {PerformanceMetrics} from '../../../../utils/models/metrics';
import {NgxEchartsDirective, provideEchartsCore} from 'ngx-echarts';
import * as echarts from 'echarts/core';
import {BarChart} from 'echarts/charts';
import {DataZoomComponent, GridComponent, LegendComponent, TooltipComponent} from 'echarts/components';
import {SVGRenderer} from 'echarts/renderers';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {FormsModule} from '@angular/forms';
import {MatAnchor} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatToolbar} from '@angular/material/toolbar';
import {RouterLink} from '@angular/router';
import {InfoCardComponent} from '../../../../utils/components/info-card/info-card.component';
import {EmptyStateComponent} from '../../../../utils/components/empty-state/empty-state.component';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';

echarts.use([BarChart, GridComponent, SVGRenderer, TooltipComponent, LegendComponent, DataZoomComponent]);


interface ScreeningMetricsResultData {
  manuelScreeningOne: boolean;
  llmScreeningOneAudited: boolean;
  llmScreeningOnePassed: boolean;
  llmScreeningOneMajority: boolean;
  manuelScreeningTwo: boolean;
  llmScreeningTwoAudited: boolean;
  llmScreeningTwoPassed: boolean;
  languageModel: string;
  embeddingModel: string;
}


interface ScreeningMetricsData {
  processes: ScreeningMetricsResultData[];
  languageModel: string;
  embeddingModel: string;
}

interface ScreeningMetricsGraphDataData {
  screeningOne: number;
  screeningOneMajority: number;
  screeningTwo: number;
  name: string;
}


@Component({
  selector: 'app-screening-metrics',
  imports: [
    NgxEchartsDirective,
    MatFormFieldModule, MatSelectModule, MatInputModule, FormsModule, MatAnchor, MatIcon, MatToolbar, RouterLink,
    InfoCardComponent, EmptyStateComponent
  ],
  templateUrl: './screening-metrics.component.html',
  styleUrl: './screening-metrics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideEchartsCore({echarts}),
  ],
})
export class ScreeningMetricsComponent {
  private readonly store: Store = inject(Store);

  private readonly metricService: MetricsService = inject(MetricsService);

  @ViewChild('echartsDiv') echartsDiv?: ElementRef;

  readonly project = this.store.selectSignal(selectSelectedProject);
  readonly projectId = computed(() => this.project()?.id);
  readonly papers = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectPapersByProjectId(projectId)) : of([] as ResearchPaperDTO[]))
    ),
    {initialValue: [] as ResearchPaperDTO[]}
  );
  readonly processes = computed(() => this.getProcesses(this.papers()));
  readonly metrics = computed(() => this.getMetrics(this.processes()));
  readonly chartOption = computed(() => {
    const graphData = this.metrics().map((metric: ScreeningMetricsData) =>
      this.calculateMetric(metric, this.selectedMetric(), this.papers().length)
    );

    const names = graphData.map(d => d.name);
    const screeningOne = graphData.map(d => d.screeningOne);
    const screeningTwo = graphData.map(d => d.screeningTwo);
    return this.generateChart(names, screeningOne, screeningTwo);
  });

  readonly selectedMetric = signal('accuracy');
  metricsNames: string[] = [
    "accuracy",
    "precision",
    "recall",
    "f1",
    "mcc",
    "kappa",
    "pabak"
  ];

  metricDescriptions: Record<string, string> = {
    accuracy: "Proportion of all predictions that were correct.",
    precision: "Proportion of positive predictions that were actually correct (manual positives / academate positives).",
    recall: "Proportion of actual positives that were correctly identified (manual positives / academate positives).",
    f1: "Harmonic mean of precision and recall, balances both metrics.",
    mcc: "Matthews Correlation Coefficient, a balanced measure for binary classification, even if classes are imbalanced.",
    kappa: "Cohen's Kappa, measures agreement between two raters corrected for chance.",
    pabak: "Prevalence-Adjusted Bias-Adjusted Kappa, an adjusted version of Kappa to reduce prevalence and bias effects."
  };

  get llmScreeningOneAuditedCount(): number {
    return this.processes().filter(p => p.llmScreeningOneAudited).length;
  }

  get llmScreeningTwoAuditedCount(): number {
    return this.processes().filter(p => p.llmScreeningTwoAudited).length;
  }

  get llmScreeningOnePassedCount(): number {
    return this.processes().filter(p => p.llmScreeningOnePassed).length;
  }

  get llmScreeningTwoPassedCount(): number {
    return this.processes().filter(p => p.llmScreeningTwoPassed).length;
  }

  get manualScreeningOneCount(): number {
    return this.processes().filter(p => p.manuelScreeningOne).length;
  }

  get manualScreeningTwoCount(): number {
    return this.processes().filter(p => p.manuelScreeningTwo).length;
  }

  calculateMetric(metric: ScreeningMetricsData, metricName: string, totalPaper: number): ScreeningMetricsGraphDataData {
    const totalPasses = metric.processes.length;

    const yTrueOne: number[] = metric.processes.map(p => p.manuelScreeningOne ? 1 : 0);
    const yPredOne: number[] = metric.processes.map(p => p.llmScreeningOnePassed ? 1 : 0);
    const screeningOne50: number[] = metric.processes.map(p => p.llmScreeningOneMajority ? 1 : 0);
    const yTrueTwo: number[] = metric.processes.map(p => p.manuelScreeningTwo ? 1 : 0);
    const yPredTwo: number[] = metric.processes.map(p => p.llmScreeningTwoPassed ? 1 : 0);
    console.log("yTrueOne", yTrueOne);
    console.log("yPredOne", yPredOne);
    console.log("yTrueTwo", yTrueTwo);
    console.log("yPredTwo", yPredTwo);
    const screeningOne: PerformanceMetrics = this.metricService.calculatePerformanceMetrics(yTrueOne, yPredOne, totalPaper, totalPasses)
    const screeningOneMajority: PerformanceMetrics = this.metricService.calculatePerformanceMetrics(screeningOne50, yPredOne, totalPaper, totalPasses)
    const screeningTwo: PerformanceMetrics = this.metricService.calculatePerformanceMetrics(yTrueTwo, yPredTwo, totalPaper, totalPasses)

    const name = `${metric.languageModel} | Embedding: ${metric.embeddingModel}`;

    const screeningOneMetric: number = screeningOne[metricName as keyof PerformanceMetrics];
    const screeningTwoMetric: number = screeningTwo[metricName as keyof PerformanceMetrics];
    const screeningOneMajorityMetric: number = screeningOneMajority[metricName as keyof PerformanceMetrics];
    return {
      screeningOne: screeningOneMetric,
      screeningTwo: screeningTwoMetric,
      screeningOneMajority: screeningOneMajorityMetric,
      name: name
    } as ScreeningMetricsGraphDataData
  }


  generateChart(names: string[], screeningOne: number[], screeningTwo: number[]): any {
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {},
      grid: {
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          show: true,
          interval: 0,
          rotate: -20,
        },
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, 0.01]
      },
      series: [
        {
          name: 'Title/Abstract screening',
          type: 'bar',
          data: screeningOne
        },
        /*  {
            name: 'Title/Abstract screening (50+)',
            type: 'bar',
            data: screeningOne50
          },*/
        {
          name: 'Full-Text screening',
          type: 'bar',
          data: screeningTwo
        }
      ]
    }
  }

  private getProcesses(papers: ResearchPaperDTO[]): ScreeningMetricsResultData[] {
    return papers.map(paper => {
      return [...paper.oldScreeningProcesses ?? [], paper.latestScreeningRun]
        .filter(p => !!p)
        .filter(p => p && p.manuelOverrideScreeningOne !== undefined && p.manuelOverrideScreeningTwo !== undefined)
        .filter(p => p.screeningOne && p.screeningTwo)
        .map(p => {
          const screeningOneResult = p.screeningOne?.result!;
          const screenOneOneAccepted = screeningOneResult.filter(r => r.passed).length;
          return {
            manuelScreeningOne: p.manuelOverrideScreeningOne,
            llmScreeningOnePassed: p.screeningOne?.passed,
            llmScreeningOneAudited: p.screeningOne?.audited,
            llmScreeningOneMajority: screenOneOneAccepted > (screeningOneResult.length / 2),
            manuelScreeningTwo: p.manuelOverrideScreeningTwo,
            llmScreeningTwoPassed: p.screeningTwo?.passed,
            llmScreeningTwoAudited: p.screeningTwo?.audited,
            languageModel: p.languageModel ?? 'Default',
            embeddingModel: p.embeddingModel ?? 'Default',
          } as ScreeningMetricsResultData
        })
    }).filter(processes => processes.length > 0).flat();
  }

  private getMetrics(processes: ScreeningMetricsResultData[]): ScreeningMetricsData[] {
    const data = processes.reduce((acc, p) => {
      const key = `${p.languageModel}|${p.embeddingModel}`;
      if (!acc[key]) {
        acc[key] = {
          processes: [],
          languageModel: p.languageModel,
          embeddingModel: p.embeddingModel,
        };
      }
      acc[key].processes.push(p);
      return acc;
    }, {} as Record<string, ScreeningMetricsData>);
    return Object.values(data);
  }
}
